import { App } from '@capacitor/app';
import { isNativeNotWeb } from '@/utils/platform-check';
import { getOtaBundleVersion } from '@/utils/ota';
import packageJson from '../../package.json';

let cached = null;
let inflight = null;

// Resolves what this install is running.
//
//   nativeVersion  the binary's CFBundleShortVersionString / versionName
//   bundleVersion  the live-update bundle, or null when running the built-in one
//   version        what to report: the bundle when there is one, else the binary
//   displayVersion the same thing said to a member — see formatDisplayVersion
//
// `version` exists so the bundle-or-binary choice is made here rather than by
// every caller. Datadog, Mixpanel and the Me page all want the same answer, and
// when they each derived it themselves they drifted — RUM tagged the bundle
// while Mixpanel kept tagging the binary, which left the two dashboards unable
// to agree on what a session ran.
/**
 * What a member should read: the binary's version, then which live update it is
 * running — `1.2.12.3`.
 *
 * That string cannot be the Capgo label. Capgo validates a bundle name as
 * strict semver — three numeric fields, no fourth — and every channel runs
 * `--no-downgrade`, which refuses any label that does not sort *above* the
 * binary's version. So `ota.yml` labels the NEXT patch and carries the build
 * and update numbers as a prerelease: `1.2.13-226.3`. Correct for Capgo, and
 * wrong on a member's screen, where it names a version they are not on. This
 * puts the two halves back in the order a person reads them.
 *
 * Only the production shape is reassembled. A staging or dev label keeps its
 * lane word, because the people who see those need to know which lane they are
 * on; and a label from before update numbers existed (`1.2.13-226`) has no
 * update to print, so it is shown as-is rather than guessed at.
 */
const PRODUCTION_BUNDLE = /^\d+\.\d+\.\d+-\d+\.(\d+)$/;
const NATIVE_VERSION = /^\d+\.\d+\.\d+$/;

export function formatDisplayVersion({ nativeVersion, bundleVersion }) {
  if (!bundleVersion) return nativeVersion;

  const update = bundleVersion.match(PRODUCTION_BUNDLE)?.[1];
  if (!update || !NATIVE_VERSION.test(nativeVersion || '')) return bundleVersion;

  return `${nativeVersion}.${update}`;
}

/**
 * A version as plain `X.Y.Z`, for the server to compare against a minimum (an
 * animation's `minAppVersion`, which follows force update and compares the
 * store binary, i.e. `nativeVersion`).
 *
 * Anything after the third field is dropped, so a live-update label like
 * `1.2.15-237.6` reads `1.2.15` -- no prerelease rules for the server to get
 * wrong.
 *
 * With no version to read, the answer depends on whether there is a binary:
 *
 *   web     no binary, and it always runs the newest JS, so the version this
 *           bundle was built from is the honest answer.
 *   native  there IS a binary and we just failed to read it (App.getInfo can
 *           reject). Reporting the bundle's version would over-report, since a
 *           live update can sit above the store binary -- the server would send
 *           a version-gated animation unflagged, the binary would have no code
 *           for it, and the member would get "Unable to load animation" instead
 *           of the update prompt. '' instead, which the server reads as no
 *           version and hides version-gated entries: the fallback animation,
 *           not a broken one.
 */
const SEMVER_BASE = /^(\d+\.\d+\.\d+)/;

export function compatVersion(version) {
  const parsed = String(version ?? '').match(SEMVER_BASE)?.[1];
  if (parsed) return parsed;
  return isNativeNotWeb() ? '' : packageJson.version;
}

/**
 * Which live update this install is running on its binary -- the `3` of
 * `1.3.7.3`. Sent to the server alongside `compatVersion`, so an animation's
 * `minAppVersion` can name a live update.
 *
 * '0' on device with the bundle built into the binary: no live update yet, and
 * saying so lets the server lock an animation rather than hide it, as it does
 * for a build that predates this header. '' on web.
 *
 * Staging is read as well as production so a gate can be tried on staging
 * first; production is the shape formatDisplayVersion shows a member.
 *
 * A dev label (`1.3.10-dev.702`) has no update counter, only the OTA workflow's
 * run number, so that is sent instead. It only goes up, which is all the
 * comparison needs: on dev, a minAppVersion of `1.3.9.703` locks an animation
 * until run 703 or later is installed. Dev bundles only ever reach the dev API
 * and CMS, so these large numbers never meet a production minimum.
 */
const LIVE_UPDATE = /^\d+\.\d+\.\d+-(?:staging\.)?\d+\.(\d+)$/;
const DEV_RUN = /^\d+\.\d+\.\d+-dev\.(\d+)$/;

export function liveUpdateNumber(bundleVersion) {
  if (!bundleVersion) return isNativeNotWeb() ? '0' : '';
  const label = String(bundleVersion);
  return label.match(LIVE_UPDATE)?.[1] ?? label.match(DEV_RUN)?.[1] ?? '';
}

// The resolved version if getAppVersion() has already finished, else null. For
// callers that have to stamp something synchronously, like Mixpanel's track hook.
export function getCachedAppVersion() {
  return cached;
}

export async function getAppVersion() {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    // Two independent bridge calls; this runs on the boot path ahead of Datadog
    // RUM and Mixpanel, so pay max() rather than sum().
    const [bundleVersion, info] = await Promise.all([
      getOtaBundleVersion().catch(() => null),
      isNativeNotWeb()
        ? App.getInfo().catch(err => {
            console.warn('[AppVersion] Capacitor App.getInfo failed:', err);
            return null;
          })
        : null,
    ]);

    const nativeVersion = info?.version || process.env.VUE_APP_VERSION || 'web';

    cached = {
      version: bundleVersion || nativeVersion,
      displayVersion: formatDisplayVersion({ nativeVersion, bundleVersion }),
      nativeVersion,
      bundleVersion,
    };
    return cached;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
